import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EventsAPI, UsersAPI } from '../api';
import { UserContext } from '../context/UserContext';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Link } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary';
import { resolveMediaUrl } from '../utils/url';

export default function EventDetail() {
  return (
    <ErrorBoundary>
      <EventDetailInner />
    </ErrorBoundary>
  );
}

function EventDetailInner() {
  const { id } = useParams();
  const { user } = useContext(UserContext);
  const isAdmin = Array.isArray(user?.roles) && user.roles.includes('admin');
  const navigate = useNavigate();

  const [ev, setEv] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [posterUrlNew, setPosterUrlNew] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  // Autocomplete for internal user invites
  const [inviteQuery, setInviteQuery] = useState('');
  const [inviteSuggestions, setInviteSuggestions] = useState([]);
  const [inviteSelectedUser, setInviteSelectedUser] = useState(null);
  const [inviteSearching, setInviteSearching] = useState(false);
  const [invites, setInvites] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      setLoading(true);
      const e = await EventsAPI.get(id);
      setEv(e);
      try {
        const a = await EventsAPI.attendees(id);
        setAttendees(a?.items || []);
      } catch {}
      try {
        const inv = await EventsAPI.invitations(id);
        setInvites(inv?.items || []);
      } catch {}
      try {
        const l = await EventsAPI.logs(id);
        setLogs(l?.items || []);
      } catch {}
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) setError('Event nicht gefunden');
      else if (status === 403) setError('Dieses Event ist privat oder du hast keine Berechtigung.');
      else setError('Event konnte nicht geladen werden');
    }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function doAction(action) {
    try {
      await EventsAPI.register(id, action);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Aktion fehlgeschlagen';
      alert(msg);
    }
  }

  async function sendInvite() {
    const inviteeUserId = inviteSelectedUser?.id;
    if (!inviteEmail && !inviteeUserId) return alert('E-Mail oder Nutzer auswählen');
    try {
      await EventsAPI.invite(id, { inviteeEmail: inviteEmail || undefined, inviteeUserId: inviteeUserId || undefined });
      setInviteEmail('');
      setInviteQuery('');
      setInviteSuggestions([]);
      setInviteSelectedUser(null);
      await load();
    } catch {
      alert('Einladung fehlgeschlagen');
    }
  }

  // Debounced search for internal users
  useEffect(() => {
    let t;
    const q = inviteQuery.trim();
    if (!q) { setInviteSuggestions([]); return; }
    setInviteSearching(true);
    t = setTimeout(async () => {
      try {
        const res = await UsersAPI.search(q);
        setInviteSuggestions(res?.items || []);
      } catch {
        setInviteSuggestions([]);
      } finally {
        setInviteSearching(false);
      }
    }, 250);
    return () => t && clearTimeout(t);
  }, [inviteQuery]);

  async function onPosterChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      await EventsAPI.uploadPoster(id, file);
      await load();
    } catch {
      alert('Poster-Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  }
  async function onPosterUrlSave() {
    if (!posterUrlNew) return;
    try {
      await EventsAPI.update(id, { posterUrl: posterUrlNew });
      setPosterUrlNew('');
      await load();
    } catch {
      alert('Poster-URL speichern fehlgeschlagen');
    }
  }
  // Compute derived values and memoized HTML before any conditional returns to keep hook order stable
  const desc = ev?.description || '';
  const safeDescriptionHtml = useMemo(() => {
    try {
      const raw = marked.parse(desc);
      return { __html: DOMPurify.sanitize(raw) };
    } catch { return { __html: '' }; }
  }, [desc]);

  if (!ev) return (
    <div className="home-page">
      <section style={{ width: '100vw', margin: 0, padding: 0 }}>
        <h2 className="hero-title" style={{ textAlign: 'center' }}>Event</h2>
      </section>
      <section style={{ width:'100%', maxWidth:1100, margin:'0 auto', padding:'0 16px 40px 16px' }}>
        {error ? (
          <div>
            <div style={{ color:'#f87171' }}>{error}</div>
            <div style={{ marginTop: 12 }}><Link to="/events">Zur Übersicht</Link></div>
          </div>
        ) : (
          <div>{loading ? 'Lädt…' : 'Keine Daten'}</div>
        )}
      </section>
    </div>
  );

  // defensive defaults
  const ended = ev.endAt ? new Date(ev.endAt) < new Date() : false;
  const capStr = ev.capacity ? `${ev.counts?.confirmed || 0}/${ev.capacity}` : 'unbegrenzt';
  const regMode = ev.registrationMode || 'open';

  return (
    <div className="home-page">
      <section style={{ width:'100vw', margin:0, padding:0, position:'relative', left:'-120px' }}>
        <h2 className="hero-title" style={{ textAlign: 'center', margin:'0 0 8px' }}>{ev.title}</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign: 'center', maxWidth:760 }}>{formatDateRange(ev.startAt, ev.endAt)} · {ev.visibility}</p>
      </section>

      <section style={{ width:'100%', maxWidth:1100, margin:'0 auto', padding:'0 16px 40px 16px', color:'#fff' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1.3fr 0.7fr', gap:16 }}>
          <div>
            {ev.posterUrl && (
              <div style={{ width:'100%', background:'#111', borderRadius:10, overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', maxHeight:'60vh' }}>
                <img
                  src={resolveMediaUrl(ev.posterUrl)}
                  alt={ev.title}
                  style={{ maxWidth:'100%', maxHeight:'60vh', width:'auto', height:'auto' }}
                />
              </div>
            )}
            <div style={{ marginTop:12 }}>
              <h3>Beschreibung</h3>
              {ev.description ? (
                <div style={{ color:'#cbd5e1' }} dangerouslySetInnerHTML={safeDescriptionHtml} />
              ) : (
                <div style={{ color:'#9ca3af' }}>—</div>
              )}
            </div>
            {Array.isArray(ev.tags) && ev.tags.length > 0 && (
              <div style={{ marginTop:12 }}>
                <h3>Tags/Kategorien</h3>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {ev.tags.map((t,idx) => <span key={idx} style={{ fontSize: 12, background:'rgba(145,70,255,0.15)', color:'#c084fc', border:'1px solid rgba(145,70,255,0.35)', padding:'2px 6px', borderRadius:6 }}>{t}</span>)}
                </div>
              </div>
            )}
            {(ev.locationType === 'venue' && ev.venueAddress) && (
              <div style={{ marginTop:12 }}>
                <h3>Ort</h3>
                <div style={{ color:'#cbd5e1' }}>{ev.venueAddress}</div>
              </div>
            )}
            {(ev.locationType === 'online' && ev.onlineUrl) && (
              <div style={{ marginTop:12 }}>
                <h3>Online</h3>
                <a href={ev.onlineUrl} target="_blank" rel="noreferrer">Zum Event-Link</a>
              </div>
            )}
            {Array.isArray(ev.customFields) && ev.customFields.length > 0 && (
              <div style={{ marginTop:12 }}>
                <h3>Zusätzliche Informationen</h3>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:6 }}>
                  {ev.customFields.map((cf, idx) => (
                    <>
                      <div key={`cfl-${idx}`} style={{ color:'#9ca3af' }}>{cf.label || `Feld ${idx+1}`}</div>
                      <div key={`cfv-${idx}`} style={{ color:'#cbd5e1' }}>{cf.value || '—'}</div>
                    </>
                  ))}
                </div>
              </div>
            )}
            {ev.typeExtras && typeof ev.typeExtras === 'object' && Object.keys(ev.typeExtras).length > 0 && (
              <div style={{ marginTop:12 }}>
                <h3>Typ-spezifische Angaben</h3>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:6 }}>
                  {Object.entries(ev.typeExtras).map(([k,v]) => (
                    <>
                      <div key={`tel-${k}`} style={{ color:'#9ca3af' }}>{k}</div>
                      <div key={`tev-${k}`} style={{ color:'#cbd5e1' }}>{String(v)}</div>
                    </>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div>
            <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12 }}>
              <div style={{ fontWeight:700 }}>Teilnahme</div>
              <div style={{ color:'#9ca3af', fontSize:13, margin:'6px 0' }}>Kapazität: {capStr}</div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                <button onClick={()=>doAction('going')} disabled={ended || regMode==='ticket'}>Ich komme</button>
                <button onClick={()=>doAction('interested')} disabled={ended || regMode==='ticket'}>Interessiert</button>
                <button onClick={()=>doAction('decline')} disabled={ended}>Absagen</button>
              </div>
              {regMode==='ticket' && (
                <div style={{ color:'#f59e0b', fontSize:12, marginTop:6 }}>Tickets erforderlich – Anmeldung derzeit deaktiviert</div>
              )}
              {regMode==='invite' && (
                <div style={{ color:'#9ca3af', fontSize:12, marginTop:6 }}>Anmeldung nur auf Einladung – du kannst dein Interesse markieren</div>
              )}
              {(isAdmin || user?.id === ev.ownerId) && (
                <div style={{ marginTop:8 }}>
                  <Link to={`/events/${ev.id}/edit`}><button>Event bearbeiten</button></Link>
                </div>
              )}
            </div>
            {(isAdmin || user?.id === ev.ownerId) && (
              <div style={{ marginTop:12, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12 }}>
                <div style={{ fontWeight:700, marginBottom:6 }}>Aktivitäten</div>
                <div style={{ color:'#9ca3af', fontSize:12, maxHeight:220, overflow:'auto' }}>
                  {(logs && logs.length > 0) ? logs.map(l => (
                    <div key={l.id} style={{ padding:'6px 0', borderBottom:'1px dashed rgba(255,255,255,0.06)' }}>
                      <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', gap:8 }}>
                        <span style={{ color:'#cbd5e1' }}>{formatDateTime(l.createdAt)}</span>
                        <span>{renderLogLine(l)}</span>
                      </div>
                    </div>
                  )) : 'Keine Aktivitäten.'}
                </div>
              </div>
            )}
            <div style={{ marginTop:12 }}>
              <Link to="/events" style={{ fontSize: 13 }}>← Zurück zur Liste</Link>
            </div>
            {(isAdmin || user?.id === ev.ownerId) && (
              <div style={{ marginTop:12, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12 }}>
                <div style={{ fontWeight:700 }}>Poster verwalten</div>
                <input type="file" accept="image/*" onChange={onPosterChange} disabled={uploading} />
                {uploading && <div style={{ color:'#9ca3af', fontSize:12, marginTop:6 }}>Upload…</div>}
                <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:8, marginTop:8 }}>
                  <input placeholder="Poster-URL" value={posterUrlNew} onChange={e=>setPosterUrlNew(e.target.value)} />
                  <button onClick={onPosterUrlSave}>Speichern</button>
                </div>
              </div>
            )}
            {(isAdmin || user?.id === ev.ownerId) && (
              <div style={{ marginTop:12, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12 }}>
                <div style={{ fontWeight:700 }}>Einladungen</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:8, marginTop:8, alignItems:'start' }}>
                  <input placeholder="E-Mail (extern)" value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} />
                  <div style={{ position:'relative' }}>
                    {inviteSelectedUser ? (
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        {inviteSelectedUser.avatarUrl && (
                          <img src={resolveMediaUrl(inviteSelectedUser.avatarUrl)} alt="avatar" style={{ width:24, height:24, borderRadius:'50%' }} />
                        )}
                        <span style={{ color:'#cbd5e1' }}>{inviteSelectedUser.displayName || inviteSelectedUser.username} <span style={{ color:'#9ca3af' }}>#{inviteSelectedUser.id}</span></span>
                        <button onClick={()=>{ setInviteSelectedUser(null); setInviteQuery(''); }} style={{ fontSize:12 }}>Entfernen</button>
                      </div>
                    ) : (
                      <input
                        placeholder="Mitglied suchen (Name, @username, E-Mail)"
                        value={inviteQuery}
                        onChange={e=>{ setInviteQuery(e.target.value); }}
                      />
                    )}
                    {!inviteSelectedUser && inviteQuery && inviteSuggestions && inviteSuggestions.length > 0 && (
                      <div style={{ position:'absolute', zIndex:10, top:'100%', left:0, right:0, background:'#0b1120', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:6, maxHeight:220, overflow:'auto' }}>
                        {inviteSuggestions.map(u => (
                          <div key={u.id} onClick={()=>{ setInviteSelectedUser(u); setInviteSuggestions([]); }} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 8px', cursor:'pointer', borderRadius:6, color:'#cbd5e1' }}>
                            {u.avatarUrl && <img src={resolveMediaUrl(u.avatarUrl)} alt="avatar" style={{ width:24, height:24, borderRadius:'50%' }} />}
                            <div style={{ display:'flex', flexDirection:'column' }}>
                              <span style={{ fontSize:14 }}>{u.displayName || u.username}</span>
                              <span style={{ fontSize:12, color:'#9ca3af' }}>#{u.id} · @{u.username}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {!inviteSelectedUser && inviteQuery && inviteSearching && (
                      <div style={{ position:'absolute', zIndex:10, top:'100%', left:0, right:0, background:'#0b1120', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:8, color:'#9ca3af' }}>Suche…</div>
                    )}
                    {!inviteSelectedUser && inviteQuery && !inviteSearching && inviteSuggestions && inviteSuggestions.length === 0 && (
                      <div style={{ position:'absolute', zIndex:10, top:'100%', left:0, right:0, background:'#0b1120', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:8, color:'#9ca3af' }}>Keine Treffer</div>
                    )}
                  </div>
                  <button onClick={sendInvite} disabled={!inviteEmail && !inviteSelectedUser}>Einladen</button>
                </div>
                <div style={{ color:'#9ca3af', fontSize:12, marginTop:8, maxHeight:200, overflow:'auto' }}>
                  {invites.length === 0 ? 'Keine Einladungen.' : invites.map(i => {
                    const inviteeLabel = i.inviteeUserId
                      ? (i.inviteeDisplayName || i.inviteeUsername || (`#${i.inviteeUserId}`))
                      : (i.inviteeEmail || '—');
                    const inviterLabel = i.inviterDisplayName || i.inviterUsername || (i.inviterId ? `#${i.inviterId}` : '—');
                    return (
                      <div key={i.id} style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:8, alignItems:'center', padding:'4px 0', borderBottom:'1px dashed rgba(255,255,255,0.06)' }}>
                        <div style={{ color:'#cbd5e1' }}>{inviteeLabel}</div>
                        <div>von {inviterLabel}</div>
                        <div style={{ color:'#cbd5e1', textAlign:'right' }}>{i.status}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {(isAdmin || user?.id === ev.ownerId) && (
              <div style={{ marginTop:12, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12 }}>
                <div style={{ fontWeight:700 }}>Externe Teilnehmer (bestätigt)</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:8, marginTop:8, alignItems:'center' }}>
                  <input type="number" min={0} value={typeof ev.externalConfirmedCount === 'number' ? ev.externalConfirmedCount : 0} onChange={async (e)=>{
                    const v = Math.max(0, parseInt(e.target.value || '0'));
                    try {
                      await EventsAPI.update(ev.id, { externalConfirmedCount: v });
                      await load();
                    } catch {
                      alert('Konnte externe Teilnehmerzahl nicht speichern');
                    }
                  }} />
                  <div style={{ color:'#9ca3af', fontSize:12 }}>Wird in bestätigte Plätze eingerechnet</div>
                </div>
              </div>
            )}
            {(ev.participantListPublic || isAdmin || user?.id === ev.ownerId) && (
              <div style={{ marginTop:12, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:12 }}>
                <div style={{ fontWeight:700 }}>Teilnehmer</div>
                <div style={{ color:'#9ca3af', fontSize:12, marginTop:6 }}>
                  {attendees.length === 0 ? 'Keine Anmeldungen.' : attendees.map(a => {
                    const label = a.displayName || a.username || a.externalEmail || (a.userId ? ('#'+a.userId) : 'Gast');
                    const right = a.isExternalGroup ? (`x ${a.externalCount}`) : a.status;
                    return (
                      <div key={a.id} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', borderBottom:'1px dashed rgba(255,255,255,0.06)' }}>
                        <div>{label}</div>
                        <div style={{ color:'#cbd5e1' }}>{right}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function formatDateRange(startAt, endAt) {
  try {
    const s = new Date(startAt);
    const e = endAt ? new Date(endAt) : null;
    const opts = { dateStyle: 'medium', timeStyle: 'short' };
    const sStr = new Intl.DateTimeFormat(undefined, opts).format(s);
    const eStr = e ? new Intl.DateTimeFormat(undefined, opts).format(e) : null;
    return eStr ? `${sStr} – ${eStr}` : sStr;
  } catch { return String(startAt); }
}

function renderLogLine(l) {
  const action = String(l.action || '');
  const map = {
    'create': 'Event erstellt',
    'update': 'Event bearbeitet',
    'cancel': 'Event abgesagt',
    'poster': 'Poster aktualisiert',
    'invite': 'Einladung gesendet',
  };
  if (action.startsWith('register:')) {
    const a = action.split(':')[1];
    return `Teilnahmestatus: ${a}`;
  }
  if (action.startsWith('poll:')) {
    const a = action.split(':')[1];
    if (a === 'create') return 'Umfrage erstellt';
    if (a === 'vote') return 'Abstimmung abgegeben';
    if (a === 'close') return 'Umfrage beendet';
    if (a === 'delete') return 'Umfrage gelöscht';
  }
  if (action === 'comment') return 'Neuer Kommentar';
  if (action === 'comment:delete') return 'Kommentar gelöscht';
  if (action === 'broadcast') return 'Nachricht an Teilnehmer gesendet';
  return map[action] || action;
}

function formatDateTime(ts) {
  try {
    const d = new Date(ts);
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short' }).format(d);
  } catch { return String(ts); }
}
